import type { AccessPolicy } from '../types/auth';

export function matchesFinancialPermission(value: string, permission: string) {
  return value === permission || value === '*' || value === 'platform:*' || value.endsWith('*') && permission.startsWith(value.slice(0, -1));
}

export function hasFinancialAccess(policy: AccessPolicy | null | undefined, permission: string, resource: { kind: 'merchant' | 'party'; id: string | null | undefined }) {
  if (!resource.id) return false;
  return policy?.grants?.some((grant) => {
    if (!grant.perms.some((value) => matchesFinancialPermission(value, permission))) return false;
    if (grant.scope === 'platform') return grant.scopeId === null;
    if (resource.kind === 'party') return grant.scope === 'party' && grant.scopeId === resource.id;
    return grant.scope === 'merchant' && grant.scopeId === resource.id
      || grant.scope === 'party' && grant.ownerMerchantIds?.includes(resource.id!) === true;
  }) ?? false;
}

export function hasSameAssignmentPartyPermissions(policy: AccessPolicy | null | undefined, permissions: string[], partyId: string | null | undefined) {
  if (!partyId) return false;
  return policy?.grants?.some((grant) =>
    (grant.scope === 'platform' && grant.scopeId === null || grant.scope === 'party' && grant.scopeId === partyId)
    && permissions.every((permission) => grant.perms.some((value) => matchesFinancialPermission(value, permission)))
  ) ?? false;
}

export const bankAdministrationPermissions = ['party_bank_accounts:view', 'party_bank_accounts:manage', 'party_bank_accounts:verify', 'party_bank_connections:view', 'party_bank_connections:manage', 'party_payment_authorities:manage'];
export function bankAdministrationPartyIds(policy: AccessPolicy | null | undefined, merchantId: string, ownerPartyId?: string | null) {
  const permits = (perms: string[]) => perms.some(value => bankAdministrationPermissions.some(permission => matchesFinancialPermission(value, permission)));
  return [...new Set((policy?.grants ?? []).flatMap(grant => {
    if (!permits(grant.perms)) return [];
    if (grant.scope === 'platform' && grant.scopeId === null && ownerPartyId) return [ownerPartyId];
    return grant.scope === 'party' && grant.scopeId && grant.ownerMerchantIds?.includes(merchantId) ? [grant.scopeId] : [];
  }))];
}
export function canEnterAccountsPayable(policy: AccessPolicy | null | undefined, merchantId: string, ownerPartyId?: string | null) {
  return hasFinancialAccess(policy, 'accounts_payable:view', { kind: 'merchant', id: merchantId }) || bankAdministrationPartyIds(policy, merchantId, ownerPartyId).length > 0;
}

export function canReadBankConnections(policy: AccessPolicy | null | undefined) {
  return policy?.grants?.some(grant => (grant.scope === 'platform' && grant.scopeId === null || grant.scope === 'party' && Boolean(grant.scopeId)) && grant.perms.some(value => matchesFinancialPermission(value, 'party_bank_connections:view'))) ?? false;
}

export function canSelfVerifyBankAccount(policy: AccessPolicy | null | undefined, payerPartyId: string | null | undefined) {
  return Boolean(payerPartyId) && (policy?.grants ?? []).some(grant =>
    grant.scope === 'party' && grant.scopeId === payerPartyId && grant.roleCode === 'owner_finance'
    && grant.perms.some(value => matchesFinancialPermission(value, 'party_bank_accounts:verify')));
}
